import React, { useState, useMemo, useCallback } from "react";
import { Heading, Subheading } from "../../design/heading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { useSupervisor } from "../../contexts/SupervisorContext";
import { Avatar } from "../../design/avatar";
import CreateSupervisorButton from "./components/CreateSupervisorButton";
import SupervisorGrid from "./components/SupervisorGrid";
import { getInitials } from "../../utils/string";
import LoadingView from "../../components/LoadingView";
import ErrorView from "../../components/ErrorView";
import { Navbar, NavbarItem } from "../../design/navbar";
import { Input } from "../../design/input";
import { Select } from "../../design/select";
import { Button } from "../../design/button";
import { XMarkIcon } from "@heroicons/react/20/solid";
import ViewToggle, { ViewMode } from "../../components/ViewToggle";
import { Badge } from "../../design/badge";
import { PrimaryLanguage, LANGUAGE_LABELS } from "@visitproof/types";

const SupervisorListPage: React.FC = () => {
  const { supervisors, loading, error } = useSupervisor();
  const [status, setStatus] = useState<"inactive" | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [languageFilter, setLanguageFilter] = useState<PrimaryLanguage | "bilingual" | "">("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  // Get unique languages from all supervisors for the filter dropdown
  const availableLanguages = useMemo(() => {
    const languageSet = new Set<PrimaryLanguage>();
    supervisors.forEach((supervisor) => {
      supervisor.languages?.forEach((lang) => {
        languageSet.add(lang.language);
      });
    });
    return Array.from(languageSet).sort((a, b) =>
      (LANGUAGE_LABELS[a] || a).localeCompare(LANGUAGE_LABELS[b] || b)
    );
  }, [supervisors]);

  // Memoized filtered supervisors
  const filteredSupervisors = useMemo(() => {
    let filtered = supervisors;

    // Status filter
    if (status === "inactive") {
      filtered = filtered.filter((supervisor) => supervisor.status === "inactive");
    } else {
      filtered = filtered.filter((supervisor) => supervisor.status !== "inactive");
    }

    // Search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((supervisor) => {
        const fullName = `${supervisor.firstName} ${supervisor.lastName}`.toLowerCase();
        const email = supervisor.email?.toLowerCase() || "";
        const phoneNumber = supervisor.phoneNumber?.toLowerCase() || "";

        return fullName.includes(searchLower) ||
               email.includes(searchLower) ||
               phoneNumber.includes(searchLower);
      });
    }

    // Language filter
    if (languageFilter) {
      if (languageFilter === "bilingual") {
        // Filter to only bilingual supervisors
        filtered = filtered.filter((supervisor) => supervisor.isBilingual);
      } else {
        // Filter to supervisors who speak a specific language
        filtered = filtered.filter((supervisor) =>
          supervisor.languages?.some((lang) => lang.language === languageFilter)
        );
      }
    }

    return filtered;
  }, [supervisors, status, searchTerm, languageFilter]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleLanguageFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setLanguageFilter(e.target.value as PrimaryLanguage | "bilingual" | "");
  }, []);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setLanguageFilter("");
  }, []);

  if (loading.list) {
    return <LoadingView />;
  }

  if (error.list) {
    return <ErrorView error={error.list} />;
  }

  const hasActiveFilters = Boolean(searchTerm.trim()) || Boolean(languageFilter);
  const hasSupervisors = supervisors.length > 0;
  const hasFilteredSupervisors = filteredSupervisors.length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <Heading>Monitors</Heading>
          {hasSupervisors && (
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {filteredSupervisors.length} of {supervisors.length} monitors
              {hasActiveFilters && " (filtered)"}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {hasSupervisors && (
            <ViewToggle view={viewMode} onViewChange={setViewMode} />
          )}
          <CreateSupervisorButton />
        </div>
      </div>

      {/* Status Tabs */}
      <Navbar>
        <NavbarItem current={status === null} onClick={() => setStatus(null)}>
          Active
        </NavbarItem>
        <NavbarItem
          current={status === "inactive"}
          onClick={() => setStatus("inactive")}
        >
          Inactive
        </NavbarItem>
      </Navbar>

      {/* Search & Language Filter */}
      {hasSupervisors && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center flex-1">
            <div className="relative flex-1 sm:max-w-sm">
              <Input
                type="search"
                placeholder="Search monitors..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>

            {/* Language Filter */}
            <div className="sm:w-48">
              <Select
                value={languageFilter}
                onChange={handleLanguageFilterChange}
              >
                <option value="">All Languages</option>
                <option value="bilingual">Bilingual Only</option>
                {availableLanguages.map((lang) => (
                  <option key={lang} value={lang}>
                    {LANGUAGE_LABELS[lang] || lang}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <Button
              plain
              onClick={clearFilters}
              className="flex items-center gap-2 text-zinc-600 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
            >
              <XMarkIcon className="w-4 h-4" />
              Clear filters
            </Button>
          )}
        </div>
      )}

      {/* Content */}
      {!hasSupervisors ? (
        <div className="mt-8 text-center">
          <Heading>No Supervisors found</Heading>
          <Subheading className="mt-4">
            Create a new supervisor to get started
          </Subheading>
        </div>
      ) : !hasFilteredSupervisors ? (
        <div className="mt-8 text-center">
          <Heading>No monitors match your search</Heading>
          <p className="mt-2 text-zinc-500 dark:text-zinc-400">
            Try adjusting your search terms or{" "}
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              clear all filters
            </button>
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <SupervisorGrid supervisors={filteredSupervisors} />
      ) : (
        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Phone Number</TableHeader>
              <TableHeader>Email</TableHeader>
              <TableHeader>Languages</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSupervisors.map((supervisor) => (
              <TableRow
                key={supervisor.id}
                href={`/supervisor/${supervisor.id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Avatar
                      src={supervisor.avatarUrl}
                      className="size-6"
                      initials={getInitials(
                        `${supervisor.firstName} ${supervisor.lastName}`,
                      )}
                    />
                    <span>{`${supervisor.firstName} ${supervisor.lastName}`}</span>
                  </div>
                </TableCell>
                <TableCell>{supervisor.phoneNumber}</TableCell>
                <TableCell>{supervisor.email}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {supervisor.languages?.length ? (
                      supervisor.languages.map((lang, idx) => (
                        <Badge
                          key={idx}
                          color={lang.isPrimary ? "blue" : "zinc"}
                        >
                          {lang.language === "other" && lang.languageOther
                            ? lang.languageOther
                            : LANGUAGE_LABELS[lang.language] || lang.language}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-zinc-400 text-sm">English</span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default SupervisorListPage;
